import React, { useState, useEffect } from 'react';
import {
  Card,
  TextContainer,
  Text,
  Button,
  LegacyStack,
  FormLayout,
  Select,
  Banner,
  Divider,
  Toast,
  TextField,
  ButtonGroup
} from '@shopify/polaris';
import { defaultTranslations } from '../../services/translationsService';
import { db } from '../../firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';

export default function LanguageSettings({ shopId, userId, settings, refreshData }) {
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [isLoading, setIsLoading] = useState(false);
  const [customTranslations, setCustomTranslations] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastError, setToastError] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // Load saved language settings when component mounts
  useEffect(() => {
    if (shopId) {
      loadLanguageSettings();
    }
  }, [shopId]);
  
  // Load language settings from Firebase
  const loadLanguageSettings = async () => {
    try {
      setIsLoading(true);
      const languageSettingsRef = doc(db, `shops/${shopId}/settings/language`);
      const docSnap = await getDoc(languageSettingsRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.defaultLanguage) {
          setSelectedLanguage(data.defaultLanguage);
        }
        if (data.customTranslations) {
          setCustomTranslations(data.customTranslations);
        }
      }
    } catch (error) {
      console.error('Error loading language settings:', error);
      showToastMessage('Failed to load language settings', true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Save language settings to Firebase
  const saveLanguageSettings = async () => {
    try {
      setIsLoading(true);
      const languageSettingsRef = doc(db, `shops/${shopId}/settings/language`);
      
      // Check if document exists
      const docSnap = await getDoc(languageSettingsRef);
      
      const data = {
        defaultLanguage: selectedLanguage,
        customTranslations,
        customLanguages,
        updatedAt: new Date(),
        updatedBy: userId
      };
      
      if (docSnap.exists()) {
        await updateDoc(languageSettingsRef, data);
      } else {
        data.createdAt = new Date();
        data.createdBy = userId;
        await setDoc(languageSettingsRef, data);
      }
      
      showToastMessage('Language settings saved successfully');
      
      // Refresh data if needed
      if (refreshData) {
        refreshData();
      }
    } catch (error) {
      console.error('Error saving language settings:', error);
      showToastMessage('Failed to save language settings', true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Helper function to show toast messages
  const showToastMessage = (message, isError = false) => {
    setToastMessage(message);
    setToastError(isError);
    setShowToast(true);
  };
  
  // Helper function to safely access nested properties
  const getTranslation = (path, fallback = '') => {
    try {
      // First check custom translations
      if (customTranslations) {
        const parts = path.split('.');
        let result = customTranslations;
        
        let foundInCustom = true;
        for (const part of parts) {
          if (result && typeof result === 'object' && part in result) {
            result = result[part];
          } else {
            foundInCustom = false;
            break;
          }
        }
        
        if (foundInCustom && result) {
          return result;
        }
      }
      
      // If not found in custom translations, check default translations
      const parts = path.split('.');
      let result = defaultTranslations;
      
      for (const part of parts) {
        if (result && typeof result === 'object' && part in result) {
          result = result[part];
        } else {
          return fallback;
        }
      }
      
      return result || fallback;
    } catch (error) {
      console.error(`Error accessing translation path: ${path}`, error);
      return fallback;
    }
  };

  // Handle custom translation changes
  const handleCustomTranslationChange = (section, language, key, value) => {
    setCustomTranslations(prevTranslations => {
      // Create a deep copy of the previous translations
      const newTranslations = JSON.parse(JSON.stringify(prevTranslations || {}));
      
      // Ensure the section exists
      if (!newTranslations[section]) {
        newTranslations[section] = {};
      }
      
      // Ensure the language exists within the section
      if (!newTranslations[section][language]) {
        newTranslations[section][language] = {};
      }
      
      // Handle nested keys (e.g., 'fitTypes.tight')
      if (key.includes('.')) {
        const parts = key.split('.');
        let current = newTranslations[section][language];
        
        // Navigate to the nested object
        for (let i = 0; i < parts.length - 1; i++) {
          const part = parts[i];
          if (!current[part]) {
            current[part] = {};
          }
          current = current[part];
        }
        
        // Set the value at the final level
        current[parts[parts.length - 1]] = value;
      } else {
        // Set the value directly if it's not nested
        newTranslations[section][language][key] = value;
      }
      
      return newTranslations;
    });
  };

  // Save custom translations to Firebase
  const saveCustomTranslations = async () => {
    try {
      setIsLoading(true);
      const languageSettingsRef = doc(db, `shops/${shopId}/settings/language`);
      
      // Check if document exists
      const docSnap = await getDoc(languageSettingsRef);
      
      const data = {
        defaultLanguage: selectedLanguage,
        customTranslations,
        updatedAt: new Date(),
        updatedBy: userId
      };
      
      if (docSnap.exists()) {
        await updateDoc(languageSettingsRef, data);
      } else {
        data.createdAt = new Date();
        data.createdBy = userId;
        await setDoc(languageSettingsRef, data);
      }
      
      showToastMessage('Custom translations saved successfully');
      setIsEditing(false);
      
      // Refresh data if needed
      if (refreshData) {
        refreshData();
      }
    } catch (error) {
      console.error('Error saving custom translations:', error);
      showToastMessage('Failed to save custom translations', true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // State for custom language management
  const [isAddingLanguage, setIsAddingLanguage] = useState(false);
  const [newLanguageCode, setNewLanguageCode] = useState('');
  const [newLanguageName, setNewLanguageName] = useState('');
  const [customLanguages, setCustomLanguages] = useState([]);
  
  // Load custom languages when component mounts
  useEffect(() => {
    if (shopId) {
      loadCustomLanguages();
    }
  }, [shopId]);
  
  // Load custom languages from Firebase
  const loadCustomLanguages = async () => {
    try {
      const languageSettingsRef = doc(db, `shops/${shopId}/settings/language`);
      const docSnap = await getDoc(languageSettingsRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.customLanguages && Array.isArray(data.customLanguages)) {
          setCustomLanguages(data.customLanguages);
        }
      }
    } catch (error) {
      console.error('Error loading custom languages:', error);
    }
  };
  
  // Add a new custom language
  const addCustomLanguage = () => {
    if (!newLanguageCode || !newLanguageName) {
      showToastMessage('Please enter both language code and name', true);
      return;
    }
    
    // Check if language code already exists
    const existingLanguage = [...defaultLanguageOptions, ...customLanguages].find(
      lang => lang.value === newLanguageCode
    );
    
    if (existingLanguage) {
      showToastMessage(`Language code '${newLanguageCode}' already exists`, true);
      return;
    }
    
    // Add the new language
    const newLanguage = { label: newLanguageName, value: newLanguageCode };
    setCustomLanguages(prev => [...prev, newLanguage]);
    
    // Reset form
    setNewLanguageCode('');
    setNewLanguageName('');
    setIsAddingLanguage(false);
    
    // Select the new language
    setSelectedLanguage(newLanguageCode);
    
    // Show success message
    showToastMessage(`Added new language: ${newLanguageName}`);
  };
  
  // Save custom languages to Firebase
  const saveCustomLanguages = async () => {
    try {
      setIsLoading(true);
      const languageSettingsRef = doc(db, `shops/${shopId}/settings/language`);
      
      // Check if document exists
      const docSnap = await getDoc(languageSettingsRef);
      
      if (docSnap.exists()) {
        await updateDoc(languageSettingsRef, { customLanguages });
      } else {
        await setDoc(languageSettingsRef, { 
          customLanguages,
          createdAt: new Date(),
          createdBy: userId
        });
      }
      
      showToastMessage('Custom languages saved successfully');
    } catch (error) {
      console.error('Error saving custom languages:', error);
      showToastMessage('Failed to save custom languages', true);
    } finally {
      setIsLoading(false);
    }
  };

  // Default language options
  const defaultLanguageOptions = [
    { label: 'English', value: 'en' },
    { label: 'French', value: 'fr' },
    { label: 'German', value: 'de' },
    { label: 'Spanish', value: 'es' },
    { label: 'Italian', value: 'it' },
    { label: 'Japanese', value: 'ja' },
    { label: 'Chinese (Simplified)', value: 'zh' }
  ];
  
  // Combined language options (default + custom)
  const languageOptions = [...defaultLanguageOptions, ...customLanguages];

  return (
    <div>
      <TextContainer>
        <Text variant="headingLg">Language Settings</Text>
        <Text>Configure language preferences for your size charts. These settings will apply to all new size charts you create.</Text>
      </TextContainer>
      
      <div style={{ marginTop: '20px' }}>
        <Card>
          <Card.Section title="Language Settings">
            <TextContainer>
              <Text>Select the language for your size charts. This will be used as the default language for all size charts.</Text>
            </TextContainer>
            
            <div style={{ marginTop: '16px' }}>
              <Select
                label="Default Language"
                options={languageOptions}
                value={selectedLanguage}
                onChange={value => setSelectedLanguage(value)}
              />
            </div>
            
            <div style={{ marginTop: '16px' }}>
              {!isAddingLanguage ? (
                <Button onClick={() => setIsAddingLanguage(true)}>
                  Add Custom Language
                </Button>
              ) : (
                <Card sectioned>
                  <FormLayout>
                    <FormLayout.Group>
                      <TextField
                        label="Language Code"
                        value={newLanguageCode}
                        onChange={setNewLanguageCode}
                        placeholder="e.g., sv, nl, pt"
                        helpText="Use standard 2-letter language codes (ISO 639-1)"
                      />
                      <TextField
                        label="Language Name"
                        value={newLanguageName}
                        onChange={setNewLanguageName}
                        placeholder="e.g., Swedish, Dutch, Portuguese"
                      />
                    </FormLayout.Group>
                    <ButtonGroup>
                      <Button onClick={() => setIsAddingLanguage(false)}>Cancel</Button>
                      <Button primary onClick={addCustomLanguage}>Add Language</Button>
                    </ButtonGroup>
                  </FormLayout>
                </Card>
              )}
            </div>
            
            {customLanguages.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <TextContainer>
                  <Text variant="headingMd">Custom Languages</Text>
                  <div style={{ marginTop: '8px' }}>
                    {customLanguages.map(lang => (
                      <div key={lang.value} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <Text>{lang.label} ({lang.value})</Text>
                      </div>
                    ))}
                  </div>
                </TextContainer>
              </div>
            )}
            
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                primary
                onClick={saveLanguageSettings}
                loading={isLoading}
              >
                Save Language Settings
              </Button>
            </div>
          </Card.Section>
          
          <Card.Section title="Translation Preview">
            <LegacyStack vertical spacing="loose">
              <TextContainer>
                <Text variant="headingMd">Translated Elements</Text>
                <Text>The following elements will be automatically translated based on the selected language:</Text>
              </TextContainer>
              
              <LegacyStack vertical spacing="tight">
                <Text variant="headingSm">Section Titles</Text>
                <div style={{ padding: '8px', backgroundColor: '#f4f6f8', borderRadius: '4px' }}>
                  <LegacyStack vertical spacing="tight">
                    <Text><strong>How to Measure:</strong> {getTranslation(`elements.${selectedLanguage}.howToMeasure`, 'How to Measure the Product\'s Size?')}</Text>
                    <Text><strong>Fit Guide:</strong> {getTranslation(`elements.${selectedLanguage}.fitGuide`, 'Fit Guide')}</Text>
                    <Text><strong>Notes:</strong> {getTranslation(`elements.${selectedLanguage}.notes`, 'Notes')}</Text>
                  </LegacyStack>
                </div>
              </LegacyStack>
              
              <LegacyStack vertical spacing="tight">
                <Text variant="headingSm">Fit Types</Text>
                <div style={{ padding: '8px', backgroundColor: '#f4f6f8', borderRadius: '4px' }}>
                  <LegacyStack vertical spacing="tight">
                    <Text><strong>Slim:</strong> {getTranslation(`elements.${selectedLanguage}.fitTypes.slim`, 'Slim')}</Text>
                    <Text><strong>Regular:</strong> {getTranslation(`elements.${selectedLanguage}.fitTypes.regular`, 'Regular')}</Text>
                    <Text><strong>Loose:</strong> {getTranslation(`elements.${selectedLanguage}.fitTypes.loose`, 'Loose')}</Text>
                    <Text><strong>Oversized:</strong> {getTranslation(`elements.${selectedLanguage}.fitTypes.oversized`, 'Oversized')}</Text>
                  </LegacyStack>
                </div>
              </LegacyStack>
              
              <LegacyStack vertical spacing="tight">
                <Text variant="headingSm">Stretch Levels</Text>
                <div style={{ padding: '8px', backgroundColor: '#f4f6f8', borderRadius: '4px' }}>
                  <LegacyStack vertical spacing="tight">
                    <Text><strong>None:</strong> {getTranslation(`elements.${selectedLanguage}.stretchLevels.none`, 'No stretch')}</Text>
                    <Text><strong>Slight:</strong> {getTranslation(`elements.${selectedLanguage}.stretchLevels.slight`, 'Slightly stretchy')}</Text>
                    <Text><strong>Moderate:</strong> {getTranslation(`elements.${selectedLanguage}.stretchLevels.moderate`, 'Moderate stretch')}</Text>
                    <Text><strong>Stretchy:</strong> {getTranslation(`elements.${selectedLanguage}.stretchLevels.stretchy`, 'Stretchy')}</Text>
                    <Text><strong>High:</strong> {getTranslation(`elements.${selectedLanguage}.stretchLevels.high`, 'Highly stretchy')}</Text>
                  </LegacyStack>
                </div>
              </LegacyStack>
            </LegacyStack>
          </Card.Section>
          
          <Card.Section title="Custom Translations">
            <TextContainer>
              <Text>Modify existing translations or add custom translations for your size charts.</Text>
            </TextContainer>
            
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => setIsEditing(!isEditing)}
              >
                {isEditing ? 'Cancel Editing' : 'Edit Translations'}
              </Button>
            </div>
            
            {isEditing && (
              <div style={{ marginTop: '16px' }}>
                <Card>
                  <Card.Section title="General Elements">
                    <FormLayout>
                      <FormLayout.Group>
                        <TextField
                          label="Size Chart Title"
                          value={customTranslations?.elements?.[selectedLanguage]?.title || getTranslation(`elements.${selectedLanguage}.title`, 'Size Chart')}
                          onChange={(value) => handleCustomTranslationChange('elements', selectedLanguage, 'title', value)}
                        />
                        <TextField
                          label="Size Guide Title"
                          value={customTranslations?.elements?.[selectedLanguage]?.sizeGuide || getTranslation(`elements.${selectedLanguage}.sizeGuide`, 'Size Guide')}
                          onChange={(value) => handleCustomTranslationChange('elements', selectedLanguage, 'sizeGuide', value)}
                        />
                      </FormLayout.Group>
                      <FormLayout.Group>
                        <TextField
                          label="Measurement Title"
                          value={customTranslations?.elements?.[selectedLanguage]?.measurements || getTranslation(`elements.${selectedLanguage}.measurements`, 'Measurements')}
                          onChange={(value) => handleCustomTranslationChange('elements', selectedLanguage, 'measurements', value)}
                        />
                        <TextField
                          label="How to Measure Title"
                          value={customTranslations?.elements?.[selectedLanguage]?.howToMeasure || getTranslation(`elements.${selectedLanguage}.howToMeasure`, 'How to Measure')}
                          onChange={(value) => handleCustomTranslationChange('elements', selectedLanguage, 'howToMeasure', value)}
                        />
                      </FormLayout.Group>
                    </FormLayout>
                  </Card.Section>
                  
                  <Card.Section title="Fit Types">
                    <FormLayout>
                      <FormLayout.Group>
                        <TextField
                          label="Fit Type Title"
                          value={customTranslations?.elements?.[selectedLanguage]?.fitType || getTranslation(`elements.${selectedLanguage}.fitType`, 'Fit Type')}
                          onChange={(value) => handleCustomTranslationChange('elements', selectedLanguage, 'fitType', value)}
                        />
                      </FormLayout.Group>
                      <FormLayout.Group>
                        <TextField
                          label="Tight"
                          value={customTranslations?.elements?.[selectedLanguage]?.fitTypes?.tight || getTranslation(`elements.${selectedLanguage}.fitTypes.tight`, 'Tight')}
                          onChange={(value) => handleCustomTranslationChange('elements', selectedLanguage, 'fitTypes.tight', value)}
                        />
                        <TextField
                          label="Fitted"
                          value={customTranslations?.elements?.[selectedLanguage]?.fitTypes?.fitted || getTranslation(`elements.${selectedLanguage}.fitTypes.fitted`, 'Fitted')}
                          onChange={(value) => handleCustomTranslationChange('elements', selectedLanguage, 'fitTypes.fitted', value)}
                        />
                      </FormLayout.Group>
                      <FormLayout.Group>
                        <TextField
                          label="Regular"
                          value={customTranslations?.elements?.[selectedLanguage]?.fitTypes?.regular || getTranslation(`elements.${selectedLanguage}.fitTypes.regular`, 'Regular')}
                          onChange={(value) => handleCustomTranslationChange('elements', selectedLanguage, 'fitTypes.regular', value)}
                        />
                        <TextField
                          label="Loose"
                          value={customTranslations?.elements?.[selectedLanguage]?.fitTypes?.loose || getTranslation(`elements.${selectedLanguage}.fitTypes.loose`, 'Loose')}
                          onChange={(value) => handleCustomTranslationChange('elements', selectedLanguage, 'fitTypes.loose', value)}
                        />
                      </FormLayout.Group>
                      <FormLayout.Group>
                        <TextField
                          label="Oversized"
                          value={customTranslations?.elements?.[selectedLanguage]?.fitTypes?.oversized || getTranslation(`elements.${selectedLanguage}.fitTypes.oversized`, 'Oversized')}
                          onChange={(value) => handleCustomTranslationChange('elements', selectedLanguage, 'fitTypes.oversized', value)}
                        />
                      </FormLayout.Group>
                    </FormLayout>
                  </Card.Section>
                  
                  <Card.Section title="Stretch Levels">
                    <FormLayout>
                      <FormLayout.Group>
                        <TextField
                          label="Stretch Level Title"
                          value={customTranslations?.elements?.[selectedLanguage]?.stretchLevel || getTranslation(`elements.${selectedLanguage}.stretchLevel`, 'Stretch Level')}
                          onChange={(value) => handleCustomTranslationChange('elements', selectedLanguage, 'stretchLevel', value)}
                        />
                      </FormLayout.Group>
                      <FormLayout.Group>
                        <TextField
                          label="No Stretch"
                          value={customTranslations?.elements?.[selectedLanguage]?.stretchLevels?.none || getTranslation(`elements.${selectedLanguage}.stretchLevels.none`, 'No stretch')}
                          onChange={(value) => handleCustomTranslationChange('elements', selectedLanguage, 'stretchLevels.none', value)}
                        />
                        <TextField
                          label="Low Stretch"
                          value={customTranslations?.elements?.[selectedLanguage]?.stretchLevels?.low || getTranslation(`elements.${selectedLanguage}.stretchLevels.low`, 'Low stretch')}
                          onChange={(value) => handleCustomTranslationChange('elements', selectedLanguage, 'stretchLevels.low', value)}
                        />
                      </FormLayout.Group>
                      <FormLayout.Group>
                        <TextField
                          label="Medium Stretch"
                          value={customTranslations?.elements?.[selectedLanguage]?.stretchLevels?.medium || getTranslation(`elements.${selectedLanguage}.stretchLevels.medium`, 'Medium stretch')}
                          onChange={(value) => handleCustomTranslationChange('elements', selectedLanguage, 'stretchLevels.medium', value)}
                        />
                        <TextField
                          label="High Stretch"
                          value={customTranslations?.elements?.[selectedLanguage]?.stretchLevels?.high || getTranslation(`elements.${selectedLanguage}.stretchLevels.high`, 'High stretch')}
                          onChange={(value) => handleCustomTranslationChange('elements', selectedLanguage, 'stretchLevels.high', value)}
                        />
                      </FormLayout.Group>
                    </FormLayout>
                  </Card.Section>
                </Card>
                
                <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                  <ButtonGroup>
                    <Button onClick={() => setIsEditing(false)}>Cancel</Button>
                    <Button
                      primary
                      onClick={saveCustomTranslations}
                      loading={isLoading}
                    >
                      Save Custom Translations
                    </Button>
                  </ButtonGroup>
                </div>
              </div>
            )}
          </Card.Section>
          
          <Card.Section title="Coming Soon">
            <TextContainer>
              <Text>In upcoming updates, this section will include:</Text>
              <ul style={{ paddingLeft: '20px', marginTop: '8px' }}>
                <li><Text>Full custom translation management for all text elements</Text></li>
                <li><Text>Import/export of translation files</Text></li>
                <li><Text>Support for additional languages</Text></li>
                <li><Text>Translation memory to reuse common translations</Text></li>
              </ul>
            </TextContainer>
          </Card.Section>
        </Card>
      </div>
      
      {showToast && (
        <Toast
          content={toastMessage}
          error={toastError}
          onDismiss={() => setShowToast(false)}
        />
      )}
    </div>
  );
}
